import { Box, Flex, FormLabel, Stack, Switch, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { SourcingEventDto, SourcingEventPatchDto } from "../../../../../../autogen/bff-api";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useSub } from "../../../../../../common/subscription/useSub";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import { resolveInclusions } from "../../../../../../common/sourcing-events/buyer/wizardSteps";

interface Props {
  eventId: string;
}

export const ProcurementScopeToggles = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();
  const terms = useProcurementTerms();
  const { state, updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const inclusions = resolveInclusions(state.eventData);
  const confirmationModal = useConfirmationModal();

  const requirementCount = state.eventData.minimumRequirements?.length ?? 0;
  const hasAwardCriteria = state.eventData.awardCriteria != null;

  // Switching a part off takes its content with it — the alternative is an event that publishes
  // krav nobody can see. Products live in their own aggregate, so that one only hides the step.
  const setMinimumRequirements = (on: boolean) => {
    if (on || requirementCount === 0) {
      updateField({ eventData: { includeMinimumRequirements: on } });
      return;
    }
    confirmationModal.show({
      title: t("Remove the minimum requirements?"),
      description: t("minimum_requirements_removed_on_toggle_off", { count: requirementCount }),
      confirmButtonText: t("Remove") ?? "Remove",
      onConfirmed: () => updateField({ eventData: { includeMinimumRequirements: false, minimumRequirements: [] } }),
    });
  };

  const setAwardCriteria = (on: boolean) => {
    if (on || !hasAwardCriteria) {
      updateField({ eventData: { includeAwardCriteria: on } });
      return;
    }
    confirmationModal.show({
      title: terms.removeAwardCriteriaTitle,
      description: terms.removeAwardCriteriaBody,
      confirmButtonText: t("Remove") ?? "Remove",
      onConfirmed: () => updateField({ eventData: { includeAwardCriteria: false, awardCriteria: null } }),
    });
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
      {confirmationModal.modal}
      <FormLabel fontWeight="bold" fontSize="md" mb="1">
        {t("What does this procurement ask for?")}
      </FormLabel>
      <Text fontSize="sm" color="smMuted" mb="4">
        {t("Switch on the parts you need. You can change this while the procurement is a draft.")}
      </Text>

      <Stack spacing="4">
        {sub.hasProductAccess && (
          <ScopeToggle
            label={t("Products")}
            help={t("Ask the suppliers to price a list of products.")}
            isChecked={inclusions.products}
            onChange={(on) => updateField({ eventData: { includeProducts: on } })}
          />
        )}
        <ScopeToggle
          label={t("Minimum requirements")}
          help={t("Requirements a supplier must answer to take part.")}
          isChecked={inclusions.minimumRequirements}
          onChange={setMinimumRequirements}
        />
        <ScopeToggle
          label={terms.awardCriteria}
          help={terms.scoreBidsAgainstCriteria}
          isChecked={inclusions.awardCriteria}
          onChange={setAwardCriteria}
        />
      </Stack>
    </Box>
  );
};

const ScopeToggle = ({
  label,
  help,
  isChecked,
  onChange,
}: {
  label: string;
  help: string;
  isChecked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <Flex align="center" justify="space-between" gap="4">
    <Box>
      <Text fontWeight="medium">{label}</Text>
      <Text fontSize="sm" color="smMuted">
        {help}
      </Text>
    </Box>
    <Switch
      size="lg"
      colorScheme="teal"
      isChecked={isChecked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      flexShrink={0}
    />
  </Flex>
);
